import type { PdfCurrencyBlockType, PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { CurrencyDisplayField } from "./currency-display-field";
import { T } from "./title-only-form.i18n";
import type { ComponentFormProps } from "./types";

// Many components have title and description fields - this is a reusable form for them
export type TitleOnlyConfig = {
  title?: string;
  description?: string;
  /** QL-414 — only on the title-only blocks that print money. */
  currencyDisplay?: PdfCurrencyDisplay;
};

type TitleOnlyFormProps = ComponentFormProps<TitleOnlyConfig> & {
  componentDescription?: string;
  /** QL-414 — set for a block that prints money (portfolio costs, wealth plan). */
  currencyBlockType?: PdfCurrencyBlockType;
};

export function TitleOnlyForm({
  config,
  onChange,
  componentDescription,
  currencyBlockType,
}: TitleOnlyFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="component-title">{t.title}</Label>
        <Input
          id="component-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="component-description">{t.description}</Label>
        <Textarea
          id="component-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      {currencyBlockType && (
        <CurrencyDisplayField
          blockType={currencyBlockType}
          value={config.currencyDisplay}
          onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
        />
      )}

      {componentDescription && (
        <div className="p-3 bg-muted/50 rounded-md">
          <p className="text-xs text-muted-foreground">{componentDescription}</p>
        </div>
      )}
    </div>
  );
}
